import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, QueryClient } from '@tanstack/react-query'; // Removed useMutation
import { Loader2, CreditCard, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { 
  listPaymentMethods,  
  // addPaymentMethod as serviceAddPaymentMethod, // Removed
  // AddPaymentMethodPayload, // Removed
  useRenewSubscription
} from '../services/subscriptionService';
import PaymentMethodForm from '../components/subscription/PaymentMethodForm'; // Added import
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const useUserPaymentMethods = () => useQuery({ queryKey: ['userPaymentMethods'], queryFn: listPaymentMethods });

// Removed NewCardDetails interface
// Removed newMutationFn
// Removed useAddPaymentMethod hook

export default function PaymentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { subscriptionId, amount } = (location.state as { subscriptionId?: string; amount?: number }) || {};
//   console.log({subscriptionId})
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(undefined);
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  // Removed newCardDetails state
  const [error, setError] = useState<string | null>(null);
  
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods} = useUserPaymentMethods();
  // Removed addCardMutation, isAddingCardLoading, addCardErrorApi
  const { mutate: processRenewalMutation, isPending: isRenewingLoading} = useRenewSubscription(queryClient);

  useEffect(() => { if (!subscriptionId || typeof amount === 'undefined') { console.warn("Subscription ID or amount not found in location state."); } }, [subscriptionId, amount, navigate]);
  useEffect(() => { if (paymentMethods && !selectedPaymentMethodId) { const defaultMethod = paymentMethods.find(pm => pm.isDefault) || paymentMethods[0]; if (defaultMethod) setSelectedPaymentMethodId(defaultMethod.id);}}, [paymentMethods, selectedPaymentMethodId]);

  const handlePaymentMethodAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['userPaymentMethods'] });
    setShowAddCardForm(false);
    // Consider if the new card should be automatically selected.
    // This might require the PaymentMethodForm or the service it calls
    // to return the newly added payment method details.
    // For now, simply refresh and close.
    toast.success(t('newPaymentMethodAddedRefresh'));
  };

  const handlePaymentMethodError = (error: Error) => {
    setError(error.message || 'Failed to add payment method.');
  };

  const handlePaymentSubmit = () => {
    if (!selectedPaymentMethodId) { setError(t('pleaseSelectPaymentMethod')); return; }
    if (!subscriptionId) { setError(t('subscriptionIdMissing')); return; }
    setError(null);

    // Ensure subscriptionId is not undefined before calling processRenewalMutation
            if (subscriptionId && selectedPaymentMethodId) {
                processRenewalMutation(
                    {
                    subscriptionId,
                    paymentMethodId: selectedPaymentMethodId,
                    },
                    {
                    onSuccess: () => {
                        toast.success(t('subscriptionRenewedSuccessfully'));
                        navigate('/Subscription');
                    },
                    onError: (e: Error) => {
                      let message = t('unknownErrorRenewal');
                      if(isAxiosError(e)) {
                        message = e.response?.data?.message || e.message || t('unknownErrorRenewal');
                      }
                      console.error('Renewal error:', message);
                      setError(message);
                    },
                    }
                );
                } else {
                setError(t('subscriptionOrPaymentMissing'));
                }

    // if (subscriptionId) {
    //   processRenewalMutation({ subscriptionId }, {
    //     onSuccess: () => { alert('Subscription renewed successfully! (Simulated)'); navigate('/subscriptions'); },
    //     onError: (e: any) => setError(e.message || "Failed to renew subscription."),
    //   });
    // } else {
    //   setError("Subscription ID is missing after check. Cannot proceed.");
    // }
  };

  // Removed handleAddCard function

  if (isLoadingPaymentMethods) return <div className="flex justify-center items-center h-screen"><Loader2 className="w-8 h-8 animate-spin" /> {t('loading')}</div>;

  return (
    <div className="container mx-auto p-4 max-w-lg">
      <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 hover:underline">&larr; {t('backButton')}</button>
      <h1 className="text-2xl font-semibold mb-6">{t('completeYourRenewal')}</h1>
      {subscriptionId && typeof amount !== 'undefined' ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-medium mb-2">{t('orderSummary')}</h2>
          <p>{t('subId')} {subscriptionId}</p>
          <p className="text-2xl font-bold">{t('amount')} ${amount ? (amount / 100).toFixed(2) : '0.00'}</p>
        </div>
      ) : <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6"><p className="font-bold">{t('missingInformation')}</p><p>{t('subscriptionDetailsMissing')}</p></div>}
      {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</p>}
      {/* {apiError && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">Error: {(apiError as Error).message}</p>} */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-medium mb-4">{t('selectPaymentMethod')}</h2>
        {paymentMethods && paymentMethods.length > 0 ? (
            <>
                {paymentMethods.map((method) => (
                <div
                    key={method.id}
                    className="flex items-center p-3 border rounded-md hover:bg-gray-50 mb-2"
                >
                    <input
                    type="radio"
                    id={`pm-${method.id}`}
                    name="paymentMethod"
                    value={method.id}
                    checked={selectedPaymentMethodId === method.id}
                    onChange={() => setSelectedPaymentMethodId(method.id)}
                    className="accent-blue-500"
                    />
                    <label
                    htmlFor={`pm-${method.id}`}
                    className="flex-grow cursor-pointer ml-2"
                    >
                    <CreditCard className="inline mr-2 w-5 h-5 text-gray-600" />
                    {method.cardBrand} {t('endingIn')} {method.last4}
                    {method.isDefault && (
                        <span className="text-xs text-green-600 ml-1">({t('default')})</span>
                    )}
                    </label>
                </div>
                ))}
            </>
        ) : !isLoadingPaymentMethods && <p>{t('noPaymentMethodsFound')}</p>}
        <Button variant="outline" onClick={() => setShowAddCardForm(!showAddCardForm)} className="mt-4">
          <PlusCircle className="mr-2 w-4 h-4" /> {showAddCardForm ? t('cancelAddingCard') : t('addNewCard')}
        </Button>
        {showAddCardForm && (
          <div className="mt-4 p-4 border rounded-md bg-gray-50 space-y-3">
            <h3 className="text-lg font-medium mb-3">{t('addNewPaymentCard')}</h3>
            <PaymentMethodForm onSuccess={handlePaymentMethodAdded} onError={handlePaymentMethodError} />
          </div>
        )}
      </div>
      <Button onClick={handlePaymentSubmit} disabled={isRenewingLoading || !selectedPaymentMethodId || showAddCardForm || !subscriptionId || typeof amount === 'undefined'} className="w-full text-lg py-3">
        {isRenewingLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />} {t('payAndRenew', { amount: amount ? (amount / 100).toFixed(2) : '0.00' })}
      </Button>
    </div>
  );
}
